import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const ITEM_HEIGHT_PX = 34;
const PANEL_PADDING_PX = 8;
const MAX_VISIBLE_ITEMS = 6;
const MIN_PANEL_HEIGHT_PX = 120;

function collectScrollContainers(fromEl) {
  const out = [];
  if (!fromEl) return out;
  let node = fromEl.parentElement;
  while (node && node !== document.documentElement) {
    const cs = window.getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(cs.overflowX) || /(auto|scroll|overlay)/.test(cs.overflowY)) {
      out.push(node);
    }
    node = node.parentElement;
  }
  return out;
}

/** Fixed menu placement: prefer below trigger; flip above when cramped. */
function computeMenuFixed(anchor) {
  const rect = anchor.getBoundingClientRect();
  const maxMenuHeight = MAX_VISIBLE_ITEMS * ITEM_HEIGHT_PX + PANEL_PADDING_PX;
  const margin = 8;
  const spaceBelow = window.innerHeight - rect.bottom - margin;
  const spaceAbove = rect.top - margin;
  const width = Math.max(rect.width, 180);
  const left = Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin));

  const openDown = spaceBelow >= MIN_PANEL_HEIGHT_PX || spaceBelow >= spaceAbove;
  if (openDown) {
    const maxHeight = Math.max(MIN_PANEL_HEIGHT_PX, Math.min(maxMenuHeight, spaceBelow));
    return { left, width, top: rect.bottom + 4, maxHeight: `${maxHeight}px` };
  }

  const maxHeight = Math.max(MIN_PANEL_HEIGHT_PX, Math.min(maxMenuHeight, spaceAbove));
  const top = Math.max(margin, rect.top - maxHeight - 4);
  return { left, width, top, maxHeight: `${maxHeight}px` };
}

function normalizeValues(value) {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.map((v) => String(v ?? "").trim()).filter(Boolean))];
}

function getTriggerLabel(options, selected, placeholder) {
  if (selected.length === 0) return placeholder;
  const labelByValue = Object.fromEntries(
    (options || []).map((o) => [String(o.value), String(o.label ?? o.value)])
  );
  const labels = selected.map((v) => labelByValue[v] || v);
  if (labels.length <= 3) return labels.join(", ");
  return `${labels.length} selected`;
}

/**
 * Generic multi-select dropdown with checkboxes.
 * Uses platform `.dropdown-wrap` / `.dropdown-option` styling; portals the menu
 * so it works inside scrollable tables.
 *
 * @param {{
 *   options: { value: string, label: string }[],
 *   value: string[],
 *   onChange: (next: string[]) => void,
 *   placeholder?: string,
 *   disabled?: boolean,
 *   title?: string,
 *   className?: string,
 *   listboxLabel?: string,
 * }} props
 */
export default function CheckboxMultiSelect({
  options = [],
  value = [],
  onChange,
  placeholder = "— Select —",
  disabled = false,
  title,
  className = "",
  listboxLabel = "Options",
}) {
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuPortalRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuFixed, setMenuFixed] = useState(null);
  const listboxId = useId();
  const selected = normalizeValues(value);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onOutside = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (menuPortalRef.current?.contains(e.target)) return;
      close();
    };
    const onEsc = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onOutside);
    window.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      window.removeEventListener("keydown", onEsc);
    };
  }, [open, close]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) {
      if (!open) setMenuFixed(null);
      return;
    }
    const update = () => {
      const anchor = triggerRef.current;
      if (!anchor) return;
      setMenuFixed(computeMenuFixed(anchor));
    };
    update();
    const scrollParents = collectScrollContainers(rootRef.current);
    scrollParents.forEach((n) => n.addEventListener("scroll", update, { passive: true }));
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      scrollParents.forEach((n) => n.removeEventListener("scroll", update));
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, options.length]);

  function toggle(optionValue) {
    const v = String(optionValue);
    const next = selected.includes(v)
      ? selected.filter((x) => x !== v)
      : [...selected, v];
    onChange(next);
  }

  const triggerLabel = getTriggerLabel(options, selected, placeholder);

  const menu = (
    <div
      id={listboxId}
      role="listbox"
      aria-multiselectable="true"
      aria-label={listboxLabel}
      className="dropdown-panel dropdown-panel--nowrap checkbox-multiselect-panel"
      style={{
        maxHeight: menuFixed?.maxHeight || `${MAX_VISIBLE_ITEMS * ITEM_HEIGHT_PX}px`,
        width: "100%",
        overflowY: "auto",
      }}
    >
      {(options || []).map((opt) => {
        const optVal = String(opt.value);
        const checked = selected.includes(optVal);
        return (
          <label key={optVal} className="dropdown-option">
            <input
              type="checkbox"
              checked={checked}
              disabled={disabled}
              onChange={() => toggle(optVal)}
            />
            <span>{opt.label}</span>
          </label>
        );
      })}
      {(options || []).length === 0 && (
        <div className="dropdown-option" style={{ opacity: 0.7 }}>
          <span>No options</span>
        </div>
      )}
    </div>
  );

  return (
    <div
      ref={rootRef}
      className={`dropdown-wrap checkbox-multiselect${className ? ` ${className}` : ""}`}
      title={title}
      style={{ minWidth: 0, width: "100%" }}
    >
      <button
        ref={triggerRef}
        type="button"
        className="dropdown-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
        style={{ width: "100%", textAlign: "left" }}
      >
        <span
          className={selected.length === 0 ? "checkbox-multiselect-placeholder" : undefined}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          {triggerLabel}
        </span>
        <span className="dropdown-arrow" aria-hidden="true">
          {open ? "▲" : "▼"}
        </span>
      </button>
      {open &&
        menuFixed != null &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={menuPortalRef}
            className="checkbox-multiselect-portal"
            style={{
              position: "fixed",
              left: menuFixed.left,
              top: menuFixed.top,
              width: menuFixed.width,
              zIndex: 12000,
            }}
          >
            {menu}
          </div>,
          document.body
        )}
    </div>
  );
}
